import {
   Flex,
   Heading,
   Text,
   Avatar,
   Image,
   useBreakpoint,
   HStack,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalCloseButton,
   useDisclosure,
   VisuallyHidden,
} from '@chakra-ui/react';
import { _js } from '@ifixit/localize';
import { FlagSection } from './FlagSection';
import { GuideDifficulty } from './DifficultyBadge';
import { GuideStats } from './GuideStats';

interface Author {
   url: string;
   name: string;
   image: string;
   authorCount: number;
   historyUrl: string;
}

interface BaseFlag {
   flagid: string;
   // Note the image here is currently an enum, not a url
   title: string;
   text: string;
   privilegeid: number;
   fullWidth?: boolean;
}

interface ImageFlag extends BaseFlag {
   image: string;
}

interface IconFlag extends BaseFlag {
   icon: string;
}

export type Flag = ImageFlag | IconFlag;

interface ViewCounts {
   today: number;
   week: number;
   month: number;
   all: number;
}

export interface GuideData {
   title: string;
   mainImage: string;
   mainImageUrl2x: string;
   mainImageUrlFull: string;
   published_date: string;
   modified_date: string;
   isTeardown: boolean;
   guideCommentCount: number;
   stepCommentCount: number;
   viewCounts: ViewCounts;
   author: Author;

   difficulty: GuideDifficulty;
   difficultyName: string;
   timeRequired: string;
   flags: Array<Flag>;

   mainImageWidth: string;
   mainImageHeight: string;
   totalTimeIso: string;
   stepCount: number;
}

export function GuideHeading({
   author,
   modified_date,
   isTeardown,
   guideCommentCount,
   stepCommentCount,
   viewCounts,
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   mainImage,
   mainImageUrl2x,
   mainImageUrlFull,
   title,
}: GuideData) {
   const breakpoint = useBreakpoint({ ssr: false });
   const isTablet = breakpoint == 'sm' || breakpoint == 'base';

   const Stats = (
      <GuideStats
         isTeardown={isTeardown}
         guideCommentCount={guideCommentCount}
         stepCommentCount={stepCommentCount}
         viewCounts={viewCounts}
      />
   );

   const { isOpen, onOpen, onClose } = useDisclosure();

   return (
      <>
         <Flex
            alignItems="flex-start"
            borderBottomWidth={1}
            paddingBottom="12px"
            marginTop={{ base: '16px', sm: '32px' }}
         >
            <Image
               src={mainImage}
               srcSet={`${mainImage} 1x, ${mainImageUrl2x} 2x`}
               onClick={onOpen}
               cursor="pointer"
               alt={title}
               maxWidth="120px"
               minWidth="120px"
               minHeight="90.5px"
               objectFit="contain"
               borderRadius="4px"
               borderWidth={1}
               marginRight="12px"
               display={{ base: 'none', sm: 'block' }}
            />
            <Modal isOpen={isOpen} onClose={onClose}>
               <ModalOverlay />
               <ModalContent width="auto" maxWidth="calc(100% - 64px)" background="none">
                  <VisuallyHidden>
                     <ModalHeader>{title}</ModalHeader>
                  </VisuallyHidden>
                  <ModalCloseButton />
                  <VisuallyHidden></VisuallyHidden>
                  <ModalBody padding={0}>
                     <Image src={mainImageUrlFull} width="100%" height="auto" />
                  </ModalBody>
               </ModalContent>
            </Modal>
            <Flex flexDir="column" width="100%">
               <Heading
                  as="h1"
                  fontSize={{ base: '24px', sm: '30px' }}
                  fontWeight="medium"
                  marginBottom="8px"
                  color="gray.800"
                  className="guide-title"
               >
                  {title}
               </Heading>
               <Flex flexDir="row" className="guide-author-section">
                  <AuthorSection {...author} modified_date={modified_date} />
                  {!isTablet && (
                     <HStack alignSelf="center" spacing="12px">
                        {Stats}
                     </HStack>
                  )}
               </Flex>
            </Flex>
         </Flex>
         {isTablet && (
            <HStack
               alignSelf="center"
               spacing="12px"
               flexWrap="wrap"
               paddingTop="12px"
               paddingBottom="12px"
               borderBottomWidth={1}
            >
               {Stats}
            </HStack>
         )}
         <FlagSection
            difficultyName={difficultyName}
            difficulty={difficulty}
            timeRequired={timeRequired}
            flags={flags}
            isTeardown={isTeardown}
         />
      </>
   );
}

function AuthorSection({
   name,
   image,
   modified_date,
   authorCount,
   url,
   historyUrl,
}: Author & { modified_date: GuideData['modified_date'] }) {
   const Contributors =
      authorCount == 1
         ? `<a href="${url}" class="guide-author">${name}</a>`
         : authorCount > 2
           ? /* Translators: %1 Start of link, %2 main author name, %3 end of link, %4 start of link, %5count of other authors, %6 end of link*/
             _js(
                '%1%2%3 and %4%5 other contributors%6',
                `<a href="${url}" class="guide-author">`,
                name,
                '</a>',
                `<a href="${historyUrl}">`,
                (authorCount - 1).toString(),
                '</a>'
             )
           : /* Translators: %1 Start of link, %2 main author name, %3 end of link, %4 start of link, %5 end of link*/
             _js(
                '%1%2%3 and %4one other contributor%5',
                `<a href="${url}" class="guide-author">`,
                name,
                '</a>',
                `<a href="${historyUrl}">`,
                '</a>'
             );

   return (
      <>
         <Avatar
            size="lg"
            name={name}
            src={image}
            width="40px"
            height="40px"
            border="2px solid"
            color="brand.500"
            background="gray.100"
            ignoreFallback={true}
         />
         <Flex flexDir="column" alignSelf="center" flexGrow={1} paddingLeft="6px">
            <Text
               margin="0px"
               color="gray.800"
               fontWeight="medium"
               sx={{ '& a': { color: 'brand.500', _hover: { textDecoration: 'none' } } }}
               dangerouslySetInnerHTML={{ __html: Contributors }}
            ></Text>
            <Text margin="0px" color="gray.500">
               {_js('Last updated on %1', modified_date)}
            </Text>
         </Flex>
      </>
   );
}
